import { APP_SETTINGS } from '../config/appSettings'
import { isRecoveredContext, isRecoveredStatement } from './recoveredContexts'
import type { NoteAsset } from '../assets/assetTypes'
import type { ActiveDraft, Note, NoteRevision } from '../domain/note'
import type { GlobalSettings } from '../domain/settings'
import type { AppEnvironment } from '../supabase/config'
import type {
  IntelligencePreferences,
  SemanticConstraint,
  StoredIntelligencePreference,
} from './legacyIntelligenceRecovery'
import {
  fingerprintRecoveryAccount,
  LOCAL_RECOVERY_EXPORT_SCHEMA_VERSION,
  type EncodedBlob,
  type ExportedAsset,
  type LocalRecoveryExport,
} from './localRecoveryExport'

export class InvalidRecoveryFileError extends Error {
  constructor() {
    super('This is not a supported miniMany local recovery copy.')
    this.name = 'InvalidRecoveryFileError'
  }
}

export class RecoveryAccountMismatchError extends Error {
  constructor() {
    super('This recovery copy belongs to a different miniMany account or environment.')
    this.name = 'RecoveryAccountMismatchError'
  }
}

export class RecoveryDraftConflictError extends Error {
  constructor() {
    super('Save or discard the current draft before importing a recovery copy.')
    this.name = 'RecoveryDraftConflictError'
  }
}

export interface LocalRecoveryImportPlan {
  notes: Note[]
  activeDraft: ActiveDraft | null
  noteRevisions: NoteRevision[]
  globalSettings: GlobalSettings | null
  assets: NoteAsset[]
  semanticConstraints?: SemanticConstraint[]
  intelligencePreferences?: StoredIntelligencePreference[]
}

export interface LocalRecoveryImportResult {
  importedNotes: number
  importedAssets: number
  importedGlobalSettings: boolean
  importedDraft: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isString(value: unknown): value is string {
  return typeof value === 'string'
}

function isNullableString(value: unknown): value is string | null {
  return value === null || isString(value)
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || isFiniteNumber(value)
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString)
}

function isUrlEntity(value: unknown): boolean {
  return isRecord(value)
    && isString(value.originalUrl)
    && (value.addedAt === undefined || (isFiniteNumber(value.addedAt) && value.addedAt >= 0))
    && isNullableString(value.canonicalUrl)
    && isNullableString(value.domain)
    && isNullableString(value.title)
    && isNullableString(value.description)
    && isNullableString(value.imageUrl)
    && isNullableNumber(value.durationSeconds)
    && isNullableString(value.siteName)
    && isNullableString(value.author)
    && isNullableNumber(value.publishedAt)
    && isNullableNumber(value.fetchedAt)
    && (value.metadataPolicyVersion === undefined || isNullableNumber(value.metadataPolicyVersion))
    && (value.metadataFetchAttemptCount === undefined || (
      isFiniteNumber(value.metadataFetchAttemptCount) && value.metadataFetchAttemptCount >= 0
    ))
    && (value.metadataNextRetryAt === undefined || isNullableNumber(value.metadataNextRetryAt))
}

function isNote(value: unknown): value is Note {
  if (!isRecord(value)) return false
  return isString(value.id)
    && isString(value.body)
    && isNullableString(value.manualTitle)
    && isString(value.derivedTitle)
    && isString(value.annotation)
    && isStringArray(value.userTags)
    && isStringArray(value.suggestedTags)
    && ['text', 'image', 'article', 'video', 'product', 'link'].includes(String(value.contentType))
    && Array.isArray(value.urls) && value.urls.every(isUrlEntity)
    && isStringArray(value.assetIds)
    && (value.suppressedUrlKeys === undefined || isStringArray(value.suppressedUrlKeys))
    && ['inbox', 'kept', 'snoozed', 'archived', 'expired', 'trash'].includes(String(value.status))
    && isNullableNumber(value.snoozedUntil)
    && isNullableNumber(value.expiresAt)
    && isNullableNumber(value.expiredAt)
    && isNullableNumber(value.trashedAt)
    && isFiniteNumber(value.createdAt)
    && isFiniteNumber(value.modifiedAt)
    && isFiniteNumber(value.capturedAt)
    && isString(value.originDeviceId)
    && isString(value.lastModifiedDeviceId)
    && isString(value.revisionId)
    && ['local', 'pending', 'failed'].includes(String(value.localSyncState))
}

function isActiveDraft(value: unknown): value is ActiveDraft {
  return isRecord(value)
    && value.key === 'active'
    && isString(value.noteId)
    && isString(value.body)
    && isString(value.annotation)
    && isStringArray(value.userTags)
    && isStringArray(value.assetIds)
    && (value.suppressedUrlKeys === undefined || isStringArray(value.suppressedUrlKeys))
    && isNullableNumber(value.expiresAt)
    && isFiniteNumber(value.createdAt)
    && isFiniteNumber(value.updatedAt)
}

function isNoteRevision(value: unknown): value is NoteRevision {
  return isRecord(value)
    && isString(value.id)
    && isString(value.noteId)
    && isNote(value.snapshot)
    && isFiniteNumber(value.modifiedAt)
    && isString(value.deviceId)
    && ['edit', 'remote-update', 'restore', 'expiry', 'delete'].includes(String(value.reason))
    && isFiniteNumber(value.clientSchemaVersion)
}

function isGlobalSettings(value: unknown): value is GlobalSettings {
  return isRecord(value)
    && value.key === 'global'
    && value.recordType === 'global'
    && value.schemaVersion === 1
    && [7, 30, 60, 90].includes(Number(value.autoExpireDays))
    && isFiniteNumber(value.modifiedAt)
    && isString(value.lastModifiedDeviceId)
    && isString(value.revisionId)
    && ['local', 'pending', 'failed'].includes(String(value.localSyncState))
}

function isSemanticConstraint(value: unknown): value is SemanticConstraint {
  return isRecord(value)
    && isString(value.id)
    && isString(value.noteId)
    && isString(value.constraintTargetKey)
    && ['exclude_item', 'not_relevant', 'confirm', 'rename'].includes(String(value.action))
    && isNullableString(value.replacementValue)
    && (value.isDeleted === undefined || typeof value.isDeleted === 'boolean')
    && (value.intelligenceEpoch === undefined || isFiniteNumber(value.intelligenceEpoch))
    && isFiniteNumber(value.modifiedAt)
    && isString(value.lastModifiedDeviceId)
    && isString(value.revisionId)
    && ['local', 'pending', 'failed', 'synced'].includes(String(value.localSyncState))
}

function isIntelligencePreference(value: unknown): value is StoredIntelligencePreference {
  if (!isRecord(value) || value.schemaVersion !== 1 || !isFiniteNumber(value.modifiedAt)) return false
  return value.key === 'account'
    && typeof value.remoteAnalysisEnabled === 'boolean'
    && isFiniteNumber(value.intelligenceEpoch)
    && isString(value.lastModifiedDeviceId)
    && isString(value.revisionId)
    && ['local', 'pending', 'failed', 'synced'].includes(String(value.localSyncState))
}

function isEncodedBlob(value: unknown): value is EncodedBlob | null {
  return value === null || (
    isRecord(value)
    && isString(value.mimeType)
    && isFiniteNumber(value.size)
    && value.size >= 0
    && isString(value.base64)
  )
}

function isExportedAsset(value: unknown): value is ExportedAsset {
  return isRecord(value)
    && isString(value.id)
    && isString(value.noteId)
    && value.kind === 'image'
    && isNullableString(value.originalFileName)
    && isString(value.originalMimeType)
    && isNullableString(value.masterMimeType)
    && isNullableString(value.thumbnailMimeType)
    && isEncodedBlob(value.sourceBlob)
    && isEncodedBlob(value.processedMasterBlob)
    && isEncodedBlob(value.thumbnailBlob)
    && isNullableNumber(value.width)
    && isNullableNumber(value.height)
    && isFiniteNumber(value.originalSize)
    && isNullableNumber(value.processedSize)
    && isNullableString(value.masterStoragePath)
    && isNullableString(value.thumbnailStoragePath)
    && ['local', 'processing', 'ready_to_upload', 'uploading', 'error', 'deleted'].includes(String(value.state))
    && [null, 'processing', 'upload', 'delete'].includes(value.errorStage as null | string)
    && isFiniteNumber(value.attempts)
    && isFiniteNumber(value.nextAttemptAt)
    && value.lastError === null
    && isFiniteNumber(value.createdAt)
    && isFiniteNumber(value.modifiedAt)
}

export function parseLocalRecoveryExport(contents: string, fileBytes: number): LocalRecoveryExport {
  if (fileBytes > APP_SETTINGS.dataContinuity.recoveryImportMaxFileBytes) {
    throw new InvalidRecoveryFileError()
  }
  let value: unknown
  try {
    value = JSON.parse(contents)
  } catch {
    throw new InvalidRecoveryFileError()
  }
  if (!isRecord(value)
    || value.format !== 'minimany-local-recovery'
    || ![1, LOCAL_RECOVERY_EXPORT_SCHEMA_VERSION].includes(Number(value.schemaVersion))
    || !isString(value.exportedAt)
    || !isRecord(value.binding)
    || ![null, 'development', 'production'].includes(value.binding.environment as null | string)
    || !isNullableString(value.binding.accountFingerprint)
    || !Array.isArray(value.notes) || !value.notes.every(isNote)
    || !(value.activeDraft === null || isActiveDraft(value.activeDraft))
    || !Array.isArray(value.pendingNoteRevisions) || !value.pendingNoteRevisions.every(isNoteRevision)
    || !Array.isArray(value.settings)
    || !value.settings.every((settings) => isGlobalSettings(settings) || isRecord(settings))
    || !Array.isArray(value.assets) || !value.assets.every(isExportedAsset)
    || !Array.isArray(value.pendingOperations)
    || (value.semanticConstraints !== undefined && (
      !Array.isArray(value.semanticConstraints) || !value.semanticConstraints.every(isSemanticConstraint)
    ))
    || (value.intelligencePreferences !== undefined && (
      !Array.isArray(value.intelligencePreferences) || !value.intelligencePreferences.every(isIntelligencePreference)
    ))
    || (value.contexts !== undefined && (!Array.isArray(value.contexts) || !value.contexts.every(isRecoveredContext)))
    || (value.contextStatements !== undefined && (!Array.isArray(value.contextStatements) || !value.contextStatements.every(isRecoveredStatement)))
    || !isRecord(value.continuity)
    || !Array.isArray(value.excluded)
  ) throw new InvalidRecoveryFileError()
  return value as unknown as LocalRecoveryExport
}

export async function verifyRecoveryBinding(
  recovery: LocalRecoveryExport,
  environment: AppEnvironment,
  userId: string,
): Promise<void> {
  if (recovery.binding.environment && recovery.binding.environment !== environment) {
    throw new RecoveryAccountMismatchError()
  }
  if (
    recovery.binding.accountFingerprint
    && recovery.binding.accountFingerprint !== await fingerprintRecoveryAccount(userId)
  ) throw new RecoveryAccountMismatchError()
}

function decodeBlob(encoded: EncodedBlob | null): Blob | null {
  if (!encoded) return null
  let binary: string
  try {
    binary = atob(encoded.base64)
  } catch {
    throw new InvalidRecoveryFileError()
  }
  if (binary.length !== encoded.size) throw new InvalidRecoveryFileError()
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return new Blob([bytes], { type: encoded.mimeType })
}

function normalizeImportedAsset(asset: ExportedAsset, now: number): NoteAsset {
  const sourceBlob = decodeBlob(asset.sourceBlob)
  const processedMasterBlob = decodeBlob(asset.processedMasterBlob)
  const thumbnailBlob = decodeBlob(asset.thumbnailBlob)
  const state = asset.state === 'deleted'
    ? 'deleted'
    : processedMasterBlob
      ? 'ready_to_upload'
      : 'local'
  return {
    ...asset,
    sourceBlob,
    processedMasterBlob,
    thumbnailBlob,
    state,
    errorStage: null,
    attempts: 0,
    nextAttemptAt: now,
    lastError: null,
  }
}

export function buildLocalRecoveryImportPlan(
  recovery: LocalRecoveryExport,
  now = Date.now(),
): LocalRecoveryImportPlan {
  const globalSettings = recovery.settings.find(isGlobalSettings) ?? null
  return {
    notes: recovery.notes.map((note) => ({ ...note, localSyncState: 'pending' })),
    activeDraft: recovery.activeDraft,
    noteRevisions: recovery.pendingNoteRevisions,
    globalSettings: globalSettings
      ? { ...globalSettings, localSyncState: 'pending' }
      : null,
    assets: recovery.assets.map((asset) => normalizeImportedAsset(asset, now)),
    semanticConstraints: (recovery.semanticConstraints ?? []).map((constraint) => ({
      ...constraint,
      isDeleted: constraint.isDeleted ?? false,
      intelligenceEpoch: constraint.intelligenceEpoch ?? 1,
      localSyncState: 'pending' as const,
    })),
    intelligencePreferences: (recovery.intelligencePreferences ?? []).map((preference) => (
      preference.key === 'account'
        ? { ...preference, localSyncState: 'pending' as const } satisfies IntelligencePreferences
        : preference
    )),
  }
}
